import { defineMessages } from 'react-intl';

const scope =
  'app.containers.WorkflowEditor.components.StageDetail.DataCollectionStage.EditQuestionsModal';

export const messages = defineMessages({
  title: {
    id: `${scope}.title`,
    defaultMessage: 'Edit questions',
    description:
      "Heading of the modal that edits a data collection stage's questions",
  },
  formTitle: {
    id: `${scope}.formTitle`,
    defaultMessage: 'Form title',
    description: 'Label for the form title shown to applicants',
  },
  formDescription: {
    id: `${scope}.formDescription`,
    defaultMessage: 'Form description',
    description: 'Label for the form description shown to applicants',
  },
  formDescriptionPlaceholder: {
    id: `${scope}.formDescriptionPlaceholder`,
    defaultMessage: 'Tell applicants what this form is for',
    description: 'Placeholder in the form description editor',
  },
  questions: {
    id: `${scope}.questions`,
    defaultMessage: 'Questions',
    description: 'Section heading above the list of questions',
  },
  questionPlaceholder: {
    id: `${scope}.questionPlaceholder`,
    defaultMessage: 'Type the question applicants will see',
    description: 'Placeholder in the question wording input',
  },
  hint: {
    id: `${scope}.hint`,
    defaultMessage: 'Hint',
    description: 'Label for the optional hint under a question',
  },
  addHint: {
    id: `${scope}.addHint`,
    defaultMessage: '+ Add hint (optional)',
    description: 'Link that reveals the hint input',
  },
  hintPlaceholder: {
    id: `${scope}.hintPlaceholder`,
    defaultMessage: 'Add a hint to help applicants answer',
    description: 'Placeholder in the hint input',
  },
  dataKey: {
    id: `${scope}.dataKey`,
    defaultMessage: 'Data key',
    description: 'Label next to the key a question stores its answer under',
  },
  enterDataKey: {
    id: `${scope}.enterDataKey`,
    defaultMessage: 'Enter data key',
    description: 'Button text when a question has no key yet',
  },
  customKeyPlaceholder: {
    id: `${scope}.customKeyPlaceholder`,
    defaultMessage: 'custom_key',
    description: 'Placeholder for a typed custom key, snake_case',
  },
  standardKeysHeading: {
    id: `${scope}.standardKeysHeading`,
    defaultMessage: 'Standard keys, no mapping required',
    description: 'Heading of the standard key picker',
  },
  standardKeysDescription: {
    id: `${scope}.standardKeysDescription`,
    defaultMessage:
      'They sync straight to the worker profile and to your HR system, with no setup work.',
    description: 'Helper text under the standard key picker heading',
  },
  searchStandardKeys: {
    id: `${scope}.searchStandardKeys`,
    defaultMessage: 'Search standard keys',
    description: 'Placeholder of the search input in the standard key picker',
  },
  noStandardKeyResults: {
    id: `${scope}.noStandardKeyResults`,
    defaultMessage: 'No standard keys match "{query}"',
    description:
      'Empty state when the picker search matches nothing; {query} is the typed text',
  },
  alreadyOnThisForm: {
    id: `${scope}.alreadyOnThisForm`,
    defaultMessage: 'already on this form',
    description:
      'Replaces the key of a standard question already used on this form',
  },
  createCustomKey: {
    id: `${scope}.createCustomKey`,
    defaultMessage: 'Other, create a custom key',
    description:
      'Footer action in the picker that switches to a typed custom key',
  },
  createCustomKeyDescription: {
    id: `${scope}.createCustomKeyDescription`,
    defaultMessage:
      'Syncs too, but reaching your HR system needs custom mapping work',
    description: 'Helper text under the create custom key action',
  },
  standardKeyBadge: {
    id: `${scope}.standardKeyBadge`,
    defaultMessage: 'Standard',
    description:
      'Badge on a key bound to a standard question, and the link that opens the picker',
  },
  standardKeyChip: {
    id: `${scope}.standardKeyChip`,
    defaultMessage: 'Standard key · no mapping required',
    description: 'Chip next to a key bound to a standard question',
  },
  standardKeyApplied: {
    id: `${scope}.standardKeyApplied`,
    defaultMessage: 'Now uses the {title} standard key',
    description:
      'Undo bar text after a question is bound to a standard key; {title} is the standard question name',
  },
  undoStandardKey: {
    id: `${scope}.undoStandardKey`,
    defaultMessage: 'Undo key change',
    description:
      'Button in the undo bar that restores the key, answer type and options the question had before',
  },
  answerType: {
    id: `${scope}.answerType`,
    defaultMessage: 'Answer type',
    description: 'Accessible label of the answer type select',
  },
  answerTypeLocked: {
    id: `${scope}.answerTypeLocked`,
    defaultMessage: 'A standard key sets the answer type',
    description:
      'Tooltip on the answer type select when a standard key fixes it',
  },
  typeTextField: {
    id: `${scope}.typeTextField`,
    defaultMessage: 'Short answer',
    description: 'Answer type: one line of text',
  },
  typeTextArea: {
    id: `${scope}.typeTextArea`,
    defaultMessage: 'Paragraph',
    description: 'Answer type: several lines of text',
  },
  typeRadio: {
    id: `${scope}.typeRadio`,
    defaultMessage: 'Multiple choice',
    description: 'Answer type: pick one option',
  },
  typeCheckboxes: {
    id: `${scope}.typeCheckboxes`,
    defaultMessage: 'Checkboxes',
    description: 'Answer type: pick several options',
  },
  typeDropdown: {
    id: `${scope}.typeDropdown`,
    defaultMessage: 'Dropdown',
    description: 'Answer type: pick one option from a dropdown',
  },
  typeDatepicker: {
    id: `${scope}.typeDatepicker`,
    defaultMessage: 'Date',
    description: 'Answer type: a date',
  },
  typeFile: {
    id: `${scope}.typeFile`,
    defaultMessage: 'File upload',
    description: 'Answer type: an uploaded file',
  },
  typeAddress: {
    id: `${scope}.typeAddress`,
    defaultMessage: 'Address',
    description: 'Answer type: a postal address',
  },
  typeOther: {
    id: `${scope}.typeOther`,
    defaultMessage: 'Other',
    description: 'Answer type not offered in the select',
  },
  restricted: {
    id: `${scope}.restricted`,
    defaultMessage: 'Restricted',
    description:
      'Checkbox and badge for an answer only secure data users can read',
  },
  restrictedDescription: {
    id: `${scope}.restrictedDescription`,
    defaultMessage: 'Only users with secure data access can read the answer',
    description: 'Tooltip on the restricted checkbox',
  },
  secureDescription: {
    id: `${scope}.secureDescription`,
    defaultMessage:
      'Stored encrypted. Only users with secure data access can read the answer',
    description:
      'Tooltip on the restricted badge of a secure standard question',
  },
  options: {
    id: `${scope}.options`,
    defaultMessage: 'Options',
    description: 'Label above the answer options of a choice question',
  },
  optionPlaceholder: {
    id: `${scope}.optionPlaceholder`,
    defaultMessage: 'Option {number}',
    description: 'Placeholder of an option input; {number} is its position',
  },
  addOption: {
    id: `${scope}.addOption`,
    defaultMessage: 'Add option',
    description: 'Button that adds an answer option',
  },
  removeOption: {
    id: `${scope}.removeOption`,
    defaultMessage: 'Remove option',
    description: 'Accessible label of the button that removes an option',
  },
  standardOptionsNote: {
    id: `${scope}.standardOptionsNote`,
    defaultMessage: 'Options come with the standard key and cannot change',
    description:
      'Helper text under the read-only options of a standard question',
  },
  moreOptions: {
    id: `${scope}.moreOptions`,
    defaultMessage: '+{count} more',
    description: 'Chip counting options not shown; {count} is the hidden count',
  },
  addQuestion: {
    id: `${scope}.addQuestion`,
    defaultMessage: 'Add question',
    description: 'Button that adds a question to the form',
  },
  removeQuestion: {
    id: `${scope}.removeQuestion`,
    defaultMessage: 'Remove question',
    description: 'Accessible label of the button that removes a question',
  },
  dragQuestion: {
    id: `${scope}.dragQuestion`,
    defaultMessage: 'Drag to reorder',
    description: 'Accessible label of the drag handle that reorders questions',
  },
  saveQuestions: {
    id: `${scope}.saveQuestions`,
    defaultMessage: 'Save questions',
    description: 'Primary button that saves the form',
  },
  noQuestionsTitle: {
    id: `${scope}.noQuestionsTitle`,
    defaultMessage: 'No questions yet',
    description: 'Empty state title when the form has no questions',
  },
  noQuestionsDescription: {
    id: `${scope}.noQuestionsDescription`,
    defaultMessage: 'Add a question to start collecting data on this stage.',
    description: 'Empty state description when the form has no questions',
  },
  loadError: {
    id: `${scope}.loadError`,
    defaultMessage:
      "We couldn't load the questions. Close this window and try again.",
    description: 'Error when the questions could not be loaded',
  },
  saveError: {
    id: `${scope}.saveError`,
    defaultMessage:
      "We couldn't save the questions. Fix the errors below and try again.",
    description: 'Error when saving the questions failed',
  },
  blockerCount: {
    id: `${scope}.blockerCount`,
    defaultMessage:
      '{count, plural, one {# field needs} other {# fields need}} your attention',
    description:
      'Alert counting fields with validation errors; {count} is the number',
  },
  errorQuestionRequired: {
    id: `${scope}.errorQuestionRequired`,
    defaultMessage: 'Enter the question text',
    description: 'Validation error when the question wording is empty',
  },
  errorKeyRequired: {
    id: `${scope}.errorKeyRequired`,
    defaultMessage: 'Choose a standard key or enter a custom key',
    description: 'Validation error when a question has no key',
  },
  errorKeyFormat: {
    id: `${scope}.errorKeyFormat`,
    defaultMessage: 'Use only lowercase letters, numbers, and underscores',
    description: 'Validation error when a custom key has invalid characters',
  },
  errorKeyDuplicate: {
    id: `${scope}.errorKeyDuplicate`,
    defaultMessage: 'Another question on this form uses this key',
    description: 'Validation error when two questions share a key',
  },
  errorOptionsRequired: {
    id: `${scope}.errorOptionsRequired`,
    defaultMessage: 'Add at least one option',
    description: 'Validation error when a choice question has no options',
  },
  errorDateRange: {
    id: `${scope}.errorDateRange`,
    defaultMessage: 'The earliest date must be on or before the latest date',
    description: 'Validation error when a date range ends before it starts',
  },
  errorFormatInvalid: {
    id: `${scope}.errorFormatInvalid`,
    defaultMessage: 'Enter a valid regular expression',
    description: 'Validation error when the answer format is not a regex',
  },
  errorHiddenValueRequired: {
    id: `${scope}.errorHiddenValueRequired`,
    defaultMessage: 'Enter the value this hidden question stores',
    description: 'Validation error when a hidden question has no value',
  },
  errorYearsOfValidation: {
    id: `${scope}.errorYearsOfValidation`,
    defaultMessage: 'Enter between {min} and {max} years',
    description:
      'Validation error on the years of history; {min} and {max} are the limits',
  },
  typeHiddenField: {
    id: `${scope}.typeHiddenField`,
    defaultMessage: 'Hidden',
    description: 'Answer type: a value stored without asking the applicant',
  },
  typeResume: {
    id: `${scope}.typeResume`,
    defaultMessage: 'Resume',
    description: 'Answer type: an uploaded resume that is parsed',
  },
  typeAddressHistory: {
    id: `${scope}.typeAddressHistory`,
    defaultMessage: 'Address history',
    description: 'Answer type: a list of past addresses',
  },
  typeEmploymentHistory: {
    id: `${scope}.typeEmploymentHistory`,
    defaultMessage: 'Employment history',
    description: 'Answer type: a list of past employers',
  },
  typeHistoryAddress: {
    id: `${scope}.typeHistoryAddress`,
    defaultMessage: 'Validated address history',
    description:
      'Answer type: past addresses covering a required number of years',
  },
  typeHistoryEmployment: {
    id: `${scope}.typeHistoryEmployment`,
    defaultMessage: 'Validated employment history',
    description:
      'Answer type: past employers covering a required number of years',
  },
  hiddenValue: {
    id: `${scope}.hiddenValue`,
    defaultMessage: 'Hidden value',
    description: 'Label of the value a hidden question stores',
  },
  yearsOfValidation: {
    id: `${scope}.yearsOfValidation`,
    defaultMessage: 'Years of history to validate',
    description: 'Label of the years a history question must cover',
  },
  addImage: {
    id: `${scope}.addImage`,
    defaultMessage: '+ Add image',
    description: 'Link that opens the file picker for a question image',
  },
  questionImage: {
    id: `${scope}.questionImage`,
    defaultMessage: 'Question image',
    description: 'Accessible label of the file input and the image preview',
  },
  removeImage: {
    id: `${scope}.removeImage`,
    defaultMessage: 'Remove image',
    description: 'Accessible label of the button that drops a question image',
  },
  imageTooLarge: {
    id: `${scope}.imageTooLarge`,
    defaultMessage: 'Choose an image under 8 MB',
    description: 'Error when the picked image is over the size limit',
  },
  imageWrongType: {
    id: `${scope}.imageWrongType`,
    defaultMessage: 'Choose a JPG or PNG image',
    description: 'Error when the picked file is not an accepted image',
  },
  validateAnswer: {
    id: `${scope}.validateAnswer`,
    defaultMessage: 'Validate answer',
    description:
      'Toggle that reveals the answer format or date range of a question',
  },
  validateAnswerDescription: {
    id: `${scope}.validateAnswerDescription`,
    defaultMessage:
      'Reject an answer that does not match a format or fall within a date range',
    description: 'Tooltip on the validate answer toggle',
  },
  answerFormat: {
    id: `${scope}.answerFormat`,
    defaultMessage: 'Answer format',
    description: 'Accessible label of the regular expression input',
  },
  answerFormatPlaceholder: {
    id: `${scope}.answerFormatPlaceholder`,
    defaultMessage: "Regular expression, for example ^\\d'{5}'$",
    description:
      'Placeholder of the regular expression input; keep the example as is',
  },
  answerFormatLocked: {
    id: `${scope}.answerFormatLocked`,
    defaultMessage: 'A standard key sets the answer format',
    description:
      'Helper text under the read-only format of a standard question',
  },
  formatMessage: {
    id: `${scope}.formatMessage`,
    defaultMessage: 'Validation error message',
    description:
      'Accessible label and placeholder of the message shown to an applicant whose answer fails the format',
  },
  removeValidation: {
    id: `${scope}.removeValidation`,
    defaultMessage: 'Remove validation',
    description: 'Accessible label of the button that clears the answer format',
  },
  dateRangeDescription: {
    id: `${scope}.dateRangeDescription`,
    defaultMessage: 'Accept only dates within this range',
    description: 'Helper text above the minimum and maximum date inputs',
  },
  minDate: {
    id: `${scope}.minDate`,
    defaultMessage: 'Earliest date',
    description: 'Label of the earliest date a date question accepts',
  },
  maxDate: {
    id: `${scope}.maxDate`,
    defaultMessage: 'Latest date',
    description: 'Label of the latest date a date question accepts',
  },
  confirmAnswer: {
    id: `${scope}.confirmAnswer`,
    defaultMessage: 'Confirm answer',
    description:
      'Toggle that makes the applicant type the answer a second time',
  },
  confirmAnswerDescription: {
    id: `${scope}.confirmAnswerDescription`,
    defaultMessage: 'Ask applicants to type the answer twice',
    description: 'Tooltip on the confirm answer toggle',
  },
  optionLabelPlaceholder: {
    id: `${scope}.optionLabelPlaceholder`,
    defaultMessage: 'Label {number}',
    description:
      'Placeholder of the label shown to applicants for an option; {number} is its position',
  },
  hideOption: {
    id: `${scope}.hideOption`,
    defaultMessage: 'Hide option',
    description:
      'Accessible label of the toggle that hides an option from applicants',
  },
  showOption: {
    id: `${scope}.showOption`,
    defaultMessage: 'Show option',
    description:
      'Accessible label of the toggle that shows a hidden option to applicants',
  },
  linkOptionBank: {
    id: `${scope}.linkOptionBank`,
    defaultMessage: 'Link an option bank',
    description: 'Placeholder of the select that links an option bank',
  },
  linkedOptionBank: {
    id: `${scope}.linkedOptionBank`,
    defaultMessage: '{name} linked',
    description:
      'Caption naming the option bank a question takes its options from; {name} is the bank',
  },
  unlinkOptionBank: {
    id: `${scope}.unlinkOptionBank`,
    defaultMessage: 'Unlink',
    description: 'Button that detaches the option bank from a question',
  },
  optionBankNote: {
    id: `${scope}.optionBankNote`,
    defaultMessage: 'Options come from the bank. Edit them in Option banks.',
    description: 'Helper text under the preview of a linked option bank',
  },
  hasLogicJumps: {
    id: `${scope}.hasLogicJumps`,
    defaultMessage: 'Has logic jumps',
    description:
      'Chip on a question that triggers a logic jump or is the target of one',
  },
  configureLogicJumps: {
    id: `${scope}.configureLogicJumps`,
    defaultMessage: 'Configure logic jumps',
    description: 'Link that opens the logic jump builder in a new tab',
  },
  configureLogicJumpsDescription: {
    id: `${scope}.configureLogicJumpsDescription`,
    defaultMessage:
      'Opens the builder in a new tab. It shows the questions saved on this stage.',
    description: 'Helper text under the logic jump builder link',
  },
  removeQuestionLogicTitle: {
    id: `${scope}.removeQuestionLogicTitle`,
    defaultMessage: 'Remove question and its logic?',
    description:
      'Title of the confirmation before removing a question with logic jumps',
  },
  removeQuestionLogicBody: {
    id: `${scope}.removeQuestionLogicBody`,
    defaultMessage:
      'Every logic jump this question triggers or is the target of is removed with it.',
    description:
      'Body of the confirmation before removing a question with logic jumps',
  },
  removeQuestionLogicConfirm: {
    id: `${scope}.removeQuestionLogicConfirm`,
    defaultMessage: 'Remove question',
    description: 'Confirm button of the remove question confirmation',
  },
  changeTypeLogicTitle: {
    id: `${scope}.changeTypeLogicTitle`,
    defaultMessage: 'Change answer type and remove logic?',
    description:
      'Title of the confirmation before changing the type of a question with logic jumps',
  },
  changeTypeLogicBody: {
    id: `${scope}.changeTypeLogicBody`,
    defaultMessage:
      'The logic jumps this question triggers depend on its current answers and are removed.',
    description:
      'Body of the confirmation before changing the type of a question with logic jumps',
  },
  changeTypeLogicConfirm: {
    id: `${scope}.changeTypeLogicConfirm`,
    defaultMessage: 'Change answer type',
    description: 'Confirm button of the change type confirmation',
  },
  linkBankLogicTitle: {
    id: `${scope}.linkBankLogicTitle`,
    defaultMessage: 'Link option bank and remove logic?',
    description:
      'Title of the confirmation before linking a bank to a question with logic jumps',
  },
  linkBankLogicBody: {
    id: `${scope}.linkBankLogicBody`,
    defaultMessage:
      'The logic jumps this question triggers depend on its typed options and are removed.',
    description:
      'Body of the confirmation before linking a bank to a question with logic jumps',
  },
  linkBankLogicConfirm: {
    id: `${scope}.linkBankLogicConfirm`,
    defaultMessage: 'Link option bank',
    description: 'Confirm button of the link bank confirmation',
  },
  unlinkBankLogicTitle: {
    id: `${scope}.unlinkBankLogicTitle`,
    defaultMessage: 'Unlink option bank and remove logic?',
    description:
      'Title of the confirmation before unlinking a bank from a question with logic jumps',
  },
  unlinkBankLogicBody: {
    id: `${scope}.unlinkBankLogicBody`,
    defaultMessage:
      'The logic jumps this question triggers depend on the bank options and are removed.',
    description:
      'Body of the confirmation before unlinking a bank from a question with logic jumps',
  },
  unlinkBankLogicConfirm: {
    id: `${scope}.unlinkBankLogicConfirm`,
    defaultMessage: 'Unlink option bank',
    description: 'Confirm button of the unlink bank confirmation',
  },
  removeOptionLogicTitle: {
    id: `${scope}.removeOptionLogicTitle`,
    defaultMessage: 'Remove option and its logic?',
    description:
      'Title of the confirmation before removing an option a logic jump triggers on',
  },
  removeOptionLogicBody: {
    id: `${scope}.removeOptionLogicBody`,
    defaultMessage:
      'A logic jump triggers on this option. It stops triggering on it, and a jump with no other option left is removed.',
    description:
      'Body of the confirmation before removing an option a logic jump triggers on',
  },
  removeOptionLogicConfirm: {
    id: `${scope}.removeOptionLogicConfirm`,
    defaultMessage: 'Remove option',
    description: 'Confirm button of the remove option confirmation',
  },
  moveLogicTitle: {
    id: `${scope}.moveLogicTitle`,
    defaultMessage: 'Move question and remove logic?',
    description:
      'Title of the confirmation before moving a question past one it is linked to by a logic jump',
  },
  moveLogicBody: {
    id: `${scope}.moveLogicBody`,
    defaultMessage:
      'A logic jump only goes forward. Moving this question past a question it is linked to removes that jump when you save.',
    description:
      'Body of the confirmation before moving a question past one it is linked to by a logic jump',
  },
  moveLogicConfirm: {
    id: `${scope}.moveLogicConfirm`,
    defaultMessage: 'Move question',
    description: 'Confirm button of the move question confirmation',
  },
});
